/** Summary card component for dashboard statistics. */
import React from "react";
import { Card, CardContent, Box, Typography, SxProps } from "@mui/material";
import {
  Computer as ServerIcon,
  CheckCircle as OnlineIcon,
  Warning as DegradedIcon,
  PhoneInTalk as CallsIcon,
  Speed as SpeedIcon,
} from "@mui/icons-material";

export type CardType =
  | "total"
  | "online"
  | "degraded"
  | "calls"
  | "cps";

interface ServerCardProps {
  title: string;
  value: number | string;
  icon?: React.ReactNode;
  color?: "primary" | "success" | "warning" | "error" | "info";
  sx?: SxProps;
}

/** Get icon and color based on card type. */
const getCardConfig = (type: CardType): { icon: React.ReactNode; color: "primary" | "success" | "warning" | "error" | "info" } => {
  switch (type) {
    case "total":
      return { icon: <ServerIcon fontSize="large" />, color: "primary" };
    case "online":
      return { icon: <OnlineIcon fontSize="large" />, color: "success" };
    case "degraded":
      return { icon: <DegradedIcon fontSize="large" />, color: "warning" };
    case "calls":
      return { icon: <CallsIcon fontSize="large" />, color: "info" };
    case "cps":
      return { icon: <SpeedIcon fontSize="large" />, color: "primary" };
    default:
      return { icon: <ServerIcon fontSize="large" />, color: "primary" };
  }
};

/** Summary card displaying a single metric with icon and value. */
export default function ServerCard({ title, value, icon, color, sx }: ServerCardProps) {
  const defaultColor: "primary" | "success" | "warning" | "error" | "info" = color || "primary";

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 4,
        },
        ...sx,
      }}
      elevation={2}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ fontWeight: 500, textTransform: "uppercase", fontSize: "0.75rem" }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: "bold", color: `${defaultColor}.main` }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: `${defaultColor}.main`,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/** Helper component for predefined card types. */
interface TypedServerCardProps {
  type: CardType;
  value: number | string;
  sx?: SxProps;
}

export function TypedServerCard({ type, value, sx }: TypedServerCardProps) {
  const config = getCardConfig(type);

  const titles: Record<CardType, string> = {
    total: "Total Servers",
    online: "Online",
    degraded: "Degraded",
    calls: "Total CC",
    cps: "Total CPS",
  };

  return <ServerCard title={titles[type]} value={value} icon={config.icon} color={config.color} sx={sx} />;
}
